import React from "react";
import { useTranslation } from "react-i18next";
import { Calculator } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { useMoneyCycleData } from "../data/moneyCycle.selectors";
import { useCurrency } from "../contexts/CurrencyContext";
import CurrencySelector from "./CurrencySelector";
import MoneyCycleChart from "./MoneyCycleChart";

const MoneyCycleModule = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { data, loading } = useMoneyCycleData();
  const { formatAmount } = useCurrency();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show prompt if no My Numbers data
  if (!data.hasData) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">{t('moneyCycle.title')}</h2>
            <p className="text-gray-400">{t('moneyCycle.trackFlow')}</p>
          </div>
          <CurrencySelector />
        </div>
        
        <Card className="border-2 border-dashed border-amber-300 bg-amber-50">
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                <Calculator className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-amber-800">{t('moneyCycle.completeNumbersFirst')}</h3>
              <p className="text-amber-700 max-w-md mx-auto">
                {t('moneyCycle.requiresData')}
              </p>
              <Button 
                onClick={() => onNavigate && onNavigate('mynumbers')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {t('moneyCycle.goToMyNumbers')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Currency Selector */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">{t('moneyCycle.title')}</h2>
          <p className="text-gray-400">{t('moneyCycle.trackFlowData')}</p>
        </div>
        <CurrencySelector />
      </div>

      {/* Money Cycle Chart */}
      <MoneyCycleChart 
        {...data} 
      />

      {/* Key Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('moneyCycle.monthlyFlow')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Income:</span>
                <span className="font-semibold text-green-600">
                  {formatAmount(data.income, data.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Expenses:</span>
                <span className="font-semibold text-red-600">
                  {formatAmount(data.expenses, data.currency)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span>Net Flow:</span>
                <span className={`font-bold ${data.income - data.expenses >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatAmount(data.income - data.expenses, data.currency)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Savings Power</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Monthly Savings:</span>
                <span className="font-semibold">
                  {formatAmount(data.savingsMonthly, data.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Savings Rate:</span>
                <span className="font-semibold">
                  {data.income > 0 ? Math.round((data.savingsMonthly / data.income) * 100) : 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Emergency Fund:</span>
                <span className="font-semibold">
                  {data.expenses > 0 ? (data.emergencyFundBalance / data.expenses).toFixed(1) : 0} months
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Wealth Building</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Passive Income:</span>
                <span className="font-semibold text-blue-600">
                  {formatAmount(data.passiveIncomeMonthly, data.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Debt Payments:</span>
                <span className="font-semibold text-red-600">
                  {formatAmount(data.totalDebtPaymentMonthly, data.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Path Status:</span>
                <span className={`font-bold ${
                  data.income > data.expenses ? 'text-green-600' : 
                  data.income === data.expenses ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {data.income > data.expenses ? 'Gains' : 
                   data.income === data.expenses ? 'Zero' : 'Loss'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MoneyCycleModule;
